/**
 * ACADS — collecting a fee, and the two views that fill themselves from it.
 *
 *   Collect a fee          find the student, type the amount received and how
 *                          it was paid; the amount is spread over the oldest
 *                          unpaid months first
 *   Collection by teacher  students down, fee months across, each cell showing
 *                          what was collected or what is still due
 *   Daily receipts         the same rows day by day, like the Kolkata cash sheet
 *
 * All three read the same fee rows, so nothing is entered twice. Only the
 * first screen is filled. A wrong payment is corrected on its line in Daily
 * receipts; a student is edited from their name on the teacher sheet.
 */
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import { jsPDF } from 'jspdf'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  amendAcadsPayment,
  cancelAcadsPayment,
  collectAcadsFee,
  createAcadsStudent,
  createAcadsTeacher,
  fetchAcadsDispatch,
  fetchAcadsLedger,
  fetchAcadsRegister,
  requestAcadsReopen,
  saveAcadsDispatchSplit,
  updateAcadsStudent
} from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const NEW_TEACHER = '__new'
// Every mode except this one needs its screenshot or receipt photo.
const CASH = 'Cash'
const LIST_LIMIT = 60
const REGISTER_PAGE = 300
const NAME_W = 190
const SHEET_NAME_W = 170
const OPENING = 'opening'
const DATE_W = 132

const useStyles = makeStyles((theme) => ({
  split: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 340px) minmax(0, 1fr)',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  panel: {
    border: '1px solid #e4e7ec',
    borderRadius: 10,
    padding: theme.spacing(2),
    minWidth: 0
  },
  list: {
    marginTop: theme.spacing(1),
    maxHeight: 'calc(100vh - 380px)',
    minHeight: 160,
    overflow: 'auto',
    border: '1px solid #e4e7ec',
    borderRadius: 6
  },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '8px 10px',
    borderBottom: '1px solid #eef0f3',
    cursor: 'pointer',
    '&:hover': { background: '#f4f6f8' }
  },
  listRowOn: { background: '#eef3fb' },
  muted: { color: theme.palette.text.secondary },
  pillDue: {
    background: '#fde9e1',
    color: '#a23c19',
    fontWeight: 600,
    height: 22
  },
  pillPaid: {
    background: '#e2f3ea',
    color: '#1c6b43',
    fontWeight: 600,
    height: 22
  },
  form: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.5)
  },
  wide: { gridColumn: '1 / -1' },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  dueBox: {
    background: '#f7f8fa',
    border: '1px solid #e4e7ec',
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    margin: theme.spacing(1.5, 0)
  },
  amount: {
    maxWidth: 260,
    '& input': { fontSize: 26, fontWeight: 700, padding: '10px 12px' }
  },
  covers: { minHeight: 24, margin: theme.spacing(1, 0, 1.5), fontWeight: 600 },
  label: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#666',
    fontWeight: 600,
    margin: theme.spacing(1.5, 0, 0.5)
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) },
  attach: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  must: { color: '#a23c19', textTransform: 'none', letterSpacing: 0 },

  figures: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(1.5)
  },
  figureKey: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#666',
    fontWeight: 600
  },
  figureVal: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  closeBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1)
  },
  overview: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: theme.spacing(1.5)
  },
  overviewBox: {
    border: '1px solid #e4e7ec',
    borderRadius: 6,
    padding: '12px 14px',
    minWidth: 0
  },
  overviewBig: {
    fontSize: 26,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  overviewOf: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary
  },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '110px minmax(0, 1fr) 80px',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1),
    fontVariantNumeric: 'tabular-nums'
  },
  track: {
    background: '#eceef1',
    borderRadius: 4,
    height: 10,
    overflow: 'hidden'
  },
  fill: { background: '#e8552b', height: '100%', borderRadius: 4 },
  lastRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto 90px',
    gap: theme.spacing(1.5),
    alignItems: 'start',
    padding: '7px 0',
    borderBottom: '1px solid #eef0f3',
    fontVariantNumeric: 'tabular-nums'
  },
  // One quiet line above the list: the total first, then how it splits.
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  summaryMain: {
    fontSize: 16,
    fontWeight: 700,
    marginRight: theme.spacing(1),
    fontVariantNumeric: 'tabular-nums'
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(1.5)
  },

  // The table has no scroll box of its own: the page scrolls, the tabs and
  // filters scroll away, and the heading row rises to the top of the window
  // and stays there (the totals row does the same at the bottom). That only
  // works while no ancestor scrolls, so cells wrap to keep the table inside
  // the page width. On a narrow screen it cannot fit; there the box scrolls
  // sideways with the first column(s) frozen, and the heading scrolls with it.
  scroll: {
    minHeight: 120,
    border: '1px solid #e4e7ec',
    borderRadius: 6,
    [theme.breakpoints.down('sm')]: { overflowX: 'auto' }
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    fontSize: 13,
    '& th, & td': {
      borderBottom: '1px solid #ececec',
      borderRight: '1px solid #f0f0f0',
      padding: '6px 8px',
      verticalAlign: 'top',
      [theme.breakpoints.down('sm')]: { whiteSpace: 'nowrap' }
    },
    '& th': {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: '#f4f6f8',
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      color: '#555',
      textAlign: 'left'
    },
    '& tfoot td': {
      position: 'sticky',
      bottom: 0,
      zIndex: 3,
      background: '#f4f6f8',
      fontWeight: 700,
      borderTop: '2px solid #cfd6de'
    }
  },
  frozen: { position: 'sticky', zIndex: 2, background: '#fbfbfc' },
  corner: { zIndex: '4 !important', background: '#f4f6f8 !important' },
  edge: { borderRight: '2px solid #cfd6de !important' },
  right: { textAlign: 'right !important' },
  // The column name is the search box, so the heading stays one row tall.
  find: {
    display: 'block',
    minWidth: 64,
    '& input': {
      padding: '5px 7px',
      fontSize: 12,
      background: '#fff',
      textTransform: 'none',
      letterSpacing: 0,
      '&::placeholder': { color: '#444', opacity: 1, fontWeight: 600 }
    }
  },
  thisMonth: { boxShadow: 'inset 0 -3px 0 #e8552b' },
  cell: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  cellSub: {
    display: 'block',
    fontSize: 11,
    opacity: 0.8,
    whiteSpace: 'normal'
  },
  cellPaid: { color: '#1c6b43' },
  cellAdvance: { background: '#e3ecf9', color: '#2b5fa8' },
  cellDue: {
    background: '#fde9e1',
    color: '#a23c19',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { boxShadow: 'inset 0 0 0 2px #a23c19' }
  },
  cellClosed: { background: '#fde9e1', color: '#a23c19' },
  cellOff: { color: '#9aa1ab' },
  // Twelve months and the opening have to fit the page (a sideways scroll
  // would stop the heading sticking to the window), so this table is tighter.
  sheetTable: {
    '& th, & td': { padding: '6px 5px' }
  },
  opening: { background: '#fff6dc !important' },
  cellOpening: {
    background: '#fff6dc',
    color: '#7a5200',
    fontWeight: 600
  },
  cellSheet: {
    background: '#f1f2f4',
    color: '#9aa1ab',
    fontSize: 11,
    textAlign: 'center'
  },
  cellClick: {
    cursor: 'pointer',
    '&:hover': { boxShadow: 'inset 0 0 0 2px currentColor' }
  },
  nameLink: {
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 280,
    paddingTop: theme.spacing(1)
  },
  dropped: {
    marginLeft: 6,
    padding: '1px 6px',
    borderRadius: 3,
    background: '#eceef1',
    color: '#667',
    fontSize: 11,
    fontWeight: 600
  },
  empty: { padding: theme.spacing(4), color: theme.palette.text.secondary }
}))

const inr = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

const errorText = (err, fallback) =>
  (err.response && err.response.data && err.response.data.detail) || fallback

// "5 Oct" for this year; the year is spelled out for any other, so a
// mistyped date stands out instead of passing as a recent one.
const shortDate = (value) => {
  const when = dayjs(value)
  return when.format(when.year() === dayjs().year() ? 'D MMM' : 'D MMM YYYY')
}

const studentKey = (s) => s.student_name.trim().toLowerCase()

const phoneDigits = (value) => String(value || '').replace(/\D/g, '')

// What a cell still owes: only months that are due now (not future ones).
const dueOf = (c) => (['due', 'part'].includes(c.state) ? c.short : 0)

// The due list as a PDF: one line per student who owes, one column per month
// that has anything due. Plain "Rs" because the PDF's built-in font has no ₹.
const rs = (v) =>
  `Rs ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

const dueListPdf = ({ title, subtitle, cols, rows, showTeacher }) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 32
  const noW = 26
  const nameW = 190
  const numW = Math.min(
    80,
    (pageW - margin * 2 - noW - nameW) / (cols.length + 1)
  )
  const tableW = noW + nameW + numW * (cols.length + 1)
  const rowH = 26
  let y = margin

  const header = () => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setFillColor(241, 242, 244)
    doc.rect(margin, y, tableW, 18, 'F')
    doc.text('#', margin + 4, y + 12)
    doc.text('Student', margin + noW, y + 12)
    cols.forEach((c, i) => {
      doc.text(c.label, margin + noW + nameW + numW * (i + 1) - 4, y + 12, {
        align: 'right'
      })
    })
    doc.text('Due', margin + tableW - 4, y + 12, { align: 'right' })
    y += 18
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text(title, margin, y + 10)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(102, 112, 133)
  doc.text(subtitle, margin, y + 26)
  doc.setTextColor(0, 0, 0)
  y += 40
  header()

  const line = (cells, bold) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(8)
    cells.forEach((v, i) => {
      doc.text(v, margin + noW + nameW + numW * (i + 1) - 4, y + 11, {
        align: 'right'
      })
    })
  }

  rows.forEach((s, n) => {
    if (y + rowH > pageH - margin) {
      doc.addPage()
      y = margin
      header()
    }
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text(String(n + 1), margin + 4, y + 11)
    doc.setFont('helvetica', 'bold')
    doc.text(
      doc.splitTextToSize(s.student_name, nameW - 6)[0],
      margin + noW,
      y + 11
    )
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(102, 112, 133)
    const sub = [
      showTeacher ? s.teacher : '',
      `Class ${s.class_label || '-'}`,
      s.centre || '-',
      s.parent_contact || ''
    ]
      .filter(Boolean)
      .join(' · ')
    doc.text(doc.splitTextToSize(sub, nameW - 6)[0], margin + noW, y + 21)
    doc.setTextColor(0, 0, 0)
    line(
      [
        ...cols.map((c) => {
          const cell = s.cells.find((x) => x.month === c.month)
          const v = cell ? dueOf(cell) : 0
          return v ? rs(v) : ''
        }),
        rs(s.due)
      ],
      false
    )
    doc.setDrawColor(228, 231, 236)
    doc.line(margin, y + rowH, margin + tableW, y + rowH)
    y += rowH
  })

  if (y + 20 > pageH - margin) {
    doc.addPage()
    y = margin
  }
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.text('Total due', margin + noW, y + 13)
  y += 2
  line(
    [
      ...cols.map((c) => rs(c.total)),
      rs(rows.reduce((sum, s) => sum + s.due, 0))
    ],
    true
  )
  return doc
}

const collectable = (c) =>
  !c.closed && c.short > 0 && ['due', 'part', 'future'].includes(c.state)

// The same rule the backend applies on save: oldest unpaid month first,
// running on into later months as advance.
const spread = (cells, amount) => {
  const covered = []
  let left = amount
  cells.forEach((c) => {
    if (left <= 0 || !collectable(c)) return
    const take = Math.min(c.short, left)
    covered.push({ ...c, take, full: take === c.short })
    left -= take
  })
  return { covered, left }
}

const useLedger = (year, withLeft) => {
  const [ledger, setLedger] = useState(null)
  const [failed, setFailed] = useState(false)
  const load = useCallback(
    () =>
      fetchAcadsLedger(year, withLeft)
        .then((data) => {
          setLedger(data)
          setFailed(false)
          return data
        })
        .catch(() => setFailed(true)),
    [year, withLeft]
  )
  useEffect(() => {
    load()
  }, [load])
  return { ledger, failed, load }
}

const Loading = ({ failed, classes, what }) =>
  failed ? (
    <Typography className={classes.empty}>
      Could not load {what}. Refresh the page to try again.
    </Typography>
  ) : (
    <div className={classes.empty}>
      <CircularProgress size={22} />
    </div>
  )

// -------------------------------------------------------------- collect a fee

const BLANK = {
  student_name: '',
  teacher: '',
  new_teacher: '',
  centre: 'TR',
  class_label: '',
  monthly_fee: '',
  parent_contact: ''
}

const AddStudent = ({
  startName,
  teachers,
  joiningLabel,
  onCancel,
  onAdded,
  classes
}) => {
  const [form, setForm] = useState({ ...BLANK, student_name: startName })
  const [busy, setBusy] = useState(false)
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })
  const isNew = form.teacher === NEW_TEACHER
  const ready =
    form.student_name.trim() &&
    form.teacher &&
    Number(form.monthly_fee) > 0 &&
    phoneDigits(form.parent_contact).length >= 10 &&
    (!isNew || form.new_teacher.trim())

  const save = async () => {
    setBusy(true)
    try {
      let teacher = form.teacher
      let teacherAdded = false
      if (isNew) {
        const created = await createAcadsTeacher(form.new_teacher.trim())
        teacher = created.uuid
        teacherAdded = true
      }
      const created = await createAcadsStudent({
        teacher,
        student_name: form.student_name.trim(),
        centre: form.centre,
        class_label: form.class_label.trim(),
        monthly_fee: form.monthly_fee,
        parent_contact: form.parent_contact.trim(),
        joining_month_raw: joiningLabel
      })
      showToast(
        `Added ${created.student_name} under ${created.teacher}.`,
        constants.SUCCESS
      )
      onAdded(created.uuid, teacherAdded)
    } catch (err) {
      showToast(errorText(err, 'Could not add the student'), constants.ERROR)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Typography variant="subtitle2">New student</Typography>
      <div className={classes.form}>
        <TextField
          className={classes.wide}
          size="small"
          variant="outlined"
          label="Student name"
          value={form.student_name}
          onChange={set('student_name')}
        />
        <TextField
          className={classes.wide}
          select
          size="small"
          variant="outlined"
          label="Teacher"
          value={form.teacher}
          onChange={set('teacher')}
        >
          {teachers.map((t) => (
            <MenuItem key={t.uuid} value={t.uuid}>
              {t.name}
            </MenuItem>
          ))}
          <MenuItem value={NEW_TEACHER}>+ Teacher is not in the list</MenuItem>
        </TextField>
        {isNew && (
          <TextField
            className={classes.wide}
            size="small"
            variant="outlined"
            label="New teacher's name"
            value={form.new_teacher}
            onChange={set('new_teacher')}
          />
        )}
        <TextField
          select
          size="small"
          variant="outlined"
          label="Centre"
          value={form.centre}
          onChange={set('centre')}
        >
          <MenuItem value="TR">Theatre Road</MenuItem>
          <MenuItem value="SL">Salt Lake</MenuItem>
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          label="Class"
          value={form.class_label}
          onChange={set('class_label')}
        />
        <TextField
          size="small"
          variant="outlined"
          label="Monthly fee"
          value={form.monthly_fee}
          onChange={(e) =>
            setForm({
              ...form,
              monthly_fee: e.target.value.replace(/[^0-9]/g, '')
            })
          }
        />
        <TextField
          size="small"
          variant="outlined"
          label="Parent phone (required)"
          value={form.parent_contact}
          onChange={set('parent_contact')}
          inputProps={{ inputMode: 'tel' }}
          helperText="10 digits. It tells two children of the same name apart."
        />
      </div>
      <Typography variant="caption" className={classes.muted}>
        Joining month is taken as {joiningLabel}, so that is the first fee due.
      </Typography>
      <div className={classes.actions}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={!ready || busy}
          onClick={save}
        >
          {busy ? 'Adding…' : 'Add and collect'}
        </Button>
      </div>
    </div>
  )
}

const CollectCard = ({ lines, picked, onPick, ledger, onSaved, classes }) => {
  const line = lines.find((l) => l.enrolment === picked) || lines[0]
  const [amount, setAmount] = useState('')
  const [mode, setMode] = useState('')
  const [datePaid, setDatePaid] = useState(ledger.today)
  // The centre whose team took the money: the student's own unless changed.
  const [collectedAt, setCollectedAt] = useState(line.centre || 'TR')
  const [file, setFile] = useState(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const fileInput = useRef(null)

  useEffect(() => {
    setAmount('')
    setReason('')
    setMode('')
    setFile(null)
    setDatePaid(ledger.today)
    setCollectedAt(line.centre || 'TR')
  }, [line.enrolment, line.centre, ledger.today])

  const monthLabel = useMemo(() => {
    const labels = { [OPENING]: 'Opening' }
    ledger.months.forEach((m) => {
      labels[m.month] = m.label
    })
    return labels
  }, [ledger.months])

  const dueCells = line.cells.filter((c) => ['due', 'part'].includes(c.state))
  const openDue = dueCells.filter((c) => !c.closed)
  const closedDue = dueCells.filter((c) => c.closed)
  const typed = Number(amount) || 0
  const { covered, left } = spread(line.cells, typed)

  let covers = (
    <span className={classes.muted} style={{ fontWeight: 400 }}>
      Type the amount and this line shows which months it covers.
    </span>
  )
  if (typed && left > 0) {
    covers = `${inr(
      left
    )} more than the fee left to collect up to March. Check the amount.`
  } else if (typed) {
    covers = `Covers: ${covered
      .map(
        (c) =>
          `${monthLabel[c.month]}${c.full ? '' : ` (part, ${inr(c.take)})`}${
            c.state === 'future' ? ' (advance)' : ''
          }`
      )
      .join(' · ')}`
  }

  const needsFile = Boolean(mode) && mode !== CASH
  // Money that leaves a month part-paid does not match the fee: say why.
  const shortOn = typed && left <= 0 ? covered.filter((c) => !c.full) : []
  const needsReason = shortOn.length > 0
  let saveLabel = `Save ${inr(typed)}`
  if (!typed) saveLabel = 'Type the amount'
  else if (left > 0) saveLabel = 'Check the amount'
  else if (needsReason && !reason.trim()) saveLabel = 'Give the reason'
  else if (!mode) saveLabel = 'Pick how they paid'
  else if (needsFile && !file) saveLabel = 'Attach the receipt'
  const ready =
    typed > 0 &&
    left <= 0 &&
    (!needsReason || reason.trim()) &&
    mode &&
    (file || !needsFile) &&
    !busy

  const save = async () => {
    setBusy(true)
    try {
      const result = await collectAcadsFee(
        {
          enrolment: line.enrolment,
          amount: typed,
          mode,
          date_paid: datePaid,
          collected_at: collectedAt,
          reason: needsReason ? reason.trim() : ''
        },
        file
      )
      showToast(
        `Saved ${inr(result.amount)} by ${result.mode} for ${result.applied
          .map((a) => a.label)
          .join(', ')}. Receipt no. ${result.receipt_no}.`,
        constants.SUCCESS
      )
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not save the payment'), constants.ERROR)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className={classes.head}>
        <Typography variant="h6">{line.student_name}</Typography>
        <Typography variant="body2" className={classes.muted}>
          Class {line.class_label || '–'} · {line.centre || '–'} · parent{' '}
          {line.parent_contact || '–'}
        </Typography>
      </div>

      {lines.length > 1 ? (
        <TextField
          select
          size="small"
          variant="outlined"
          label="Fee for which teacher?"
          style={{ minWidth: 280, marginTop: 12 }}
          value={line.enrolment}
          onChange={(e) => onPick(e.target.value)}
        >
          {lines.map((l) => (
            <MenuItem key={l.enrolment} value={l.enrolment}>
              {l.teacher} · {l.due ? `${inr(l.due)} due` : 'paid'}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Typography variant="body2" className={classes.muted}>
          Teacher: {line.teacher}
        </Typography>
      )}

      <div className={classes.dueBox}>
        <div className={classes.head}>
          <span>
            Monthly fee <b>{inr(line.monthly_fee)}</b>
          </span>
          <span>
            {openDue.length ? (
              <>
                <Chip
                  size="small"
                  className={classes.pillDue}
                  label={`Due ${inr(
                    openDue.reduce((sum, c) => sum + c.short, 0)
                  )}`}
                />{' '}
                for{' '}
                {openDue
                  .map(
                    (c) =>
                      `${monthLabel[c.month]}${
                        c.state === 'part' ? ' (balance)' : ''
                      }`
                  )
                  .join(', ')}
              </>
            ) : (
              <Chip
                size="small"
                className={classes.pillPaid}
                label="Nothing due"
              />
            )}
          </span>
        </div>
        {closedDue.length > 0 && (
          <Typography variant="caption" className={classes.muted}>
            {closedDue.map((c) => monthLabel[c.month]).join(', ')} is unpaid but
            signed off by HQ, so it cannot be collected here until HQ reopens
            the month.
          </Typography>
        )}
      </div>

      <div className={classes.label}>Amount received (₹)</div>
      <TextField
        className={classes.amount}
        variant="outlined"
        placeholder="0"
        autoFocus
        value={amount}
        onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
        inputProps={{ inputMode: 'numeric' }}
      />
      <div className={classes.covers}>{covers}</div>
      {needsReason && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Reason the amount is not the full fee"
          placeholder="e.g. rest next week, sibling discount"
          style={{ marginTop: 12 }}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={!reason.trim()}
          helperText={`${shortOn
            .map((c) => monthLabel[c.month])
            .join(', ')} will stay part-paid, so a reason is needed.`}
        />
      )}

      <div className={classes.label}>How did they pay?</div>
      <div className={classes.chips}>
        {ledger.modes.map((m) => (
          <Chip
            key={m}
            label={m}
            clickable
            color={mode === m ? 'primary' : 'default'}
            onClick={() => setMode(m)}
          />
        ))}
      </div>

      <div className={classes.label}>Collected at</div>
      <div className={classes.chips}>
        {[
          ['TR', 'Theatre Road'],
          ['SL', 'Salt Lake']
        ].map(([value, label]) => (
          <Chip
            key={value}
            label={label}
            clickable
            color={collectedAt === value ? 'primary' : 'default'}
            onClick={() => setCollectedAt(value)}
          />
        ))}
      </div>

      <div className={classes.label}>
        Payment screenshot or receipt photo{' '}
        <span className={classes.must}>
          {needsFile
            ? `· needed for ${mode}`
            : '· needed for everything except cash'}
        </span>
      </div>
      <div className={classes.attach}>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            setFile(e.target.files[0] || null)
            e.target.value = ''
          }}
        />
        <Button
          size="small"
          variant={needsFile && !file ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => fileInput.current.click()}
        >
          {file ? 'Change photo' : 'Attach photo'}
        </Button>
        {file ? (
          <Chip label={file.name} onDelete={() => setFile(null)} />
        ) : (
          <Typography variant="caption" className={classes.muted}>
            A photo or screenshot (JPG or PNG). PDFs are not accepted yet.
          </Typography>
        )}
      </div>

      <div className={classes.actions}>
        <TextField
          type="date"
          size="small"
          variant="outlined"
          label="Date received"
          value={datePaid}
          onChange={(e) => setDatePaid(e.target.value || ledger.today)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: ledger.today }}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!ready}
          onClick={save}
        >
          {busy ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </div>
  )
}

// What the right side shows until a student is picked: where the day and the
// month stand. It reads the same receipts and ledger as the other tabs.
const CollectOverview = ({
  ledger,
  teacher,
  teachers,
  onSeeReceipts,
  classes
}) => {
  const [rows, setRows] = useState(null)
  // `ledger` is replaced after every save, so the day's figures follow it.
  useEffect(() => {
    fetchAcadsRegister()
      .then((data) => setRows(data.rows))
      .catch(() => setRows([]))
  }, [ledger])

  const today = (rows || []).filter((r) => r.date_paid === ledger.today)
  const byMode = {}
  today.forEach((r) => {
    byMode[r.mode || 'No mode'] = (byMode[r.mode || 'No mode'] || 0) + r.amount
  })
  const modes = Object.keys(byMode).sort((x, y) => byMode[y] - byMode[x])
  const todayTotal = today.reduce((sum, r) => sum + r.amount, 0)
  const top = modes.length ? byMode[modes[0]] : 0
  // one collection can pay several months: count it once
  const receipts = new Set(today.map((r) => r.receipt_no || r.uuid)).size

  // The month so far, from each student's cell for the current month.
  let expected = 0
  let collected = 0
  let paid = 0
  let toPay = 0
  ledger.students
    .filter((s) => !teacher || s.teacher_uuid === teacher)
    .forEach((s) => {
      const cell = s.cells.find((c) => c.month === ledger.current_month)
      if (!cell || !cell.fee) return
      expected += cell.fee
      collected += cell.paid || 0
      if (cell.short > 0) toPay += 1
      else paid += 1
    })
  const share = expected ? Math.min(100, (collected / expected) * 100) : 0
  const monthName = dayjs(`${ledger.current_month}-01`).format('MMMM')
  const picked = teachers.find((t) => t.uuid === teacher)

  // The last few collections saved, newest first, one line per receipt number.
  const last = []
  ;[...(rows || [])]
    .filter((r) => r.entered_at)
    .sort((x, y) => (x.entered_at < y.entered_at ? 1 : -1))
    .forEach((r) => {
      const same = r.receipt_no && last.find((l) => l.no === r.receipt_no)
      if (same) {
        same.amount += r.amount
        same.months.push(r.month_label)
      } else if (last.length < 5) {
        last.push({
          key: r.uuid,
          no: r.receipt_no,
          name: r.student_name,
          months: [r.month_label],
          mode: r.mode,
          amount: r.amount,
          at: r.entered_at
        })
      }
    })

  return (
    <div>
      <Typography className={classes.muted} style={{ marginBottom: 12 }}>
        Pick a student on the left to record a payment. Until then, this is
        where the day stands.
      </Typography>
      <div className={classes.overview}>
        <div className={classes.overviewBox}>
          <div className={classes.figureKey}>
            Collected today · {shortDate(ledger.today)}
          </div>
          <div className={classes.overviewBig}>{inr(todayTotal)}</div>
          <Typography variant="body2" className={classes.muted}>
            {receipts} receipt{receipts === 1 ? '' : 's'}
            {byMode[CASH] ? (
              <>
                {' '}
                · cash to be in the box tonight: <b>{inr(byMode[CASH])}</b>
              </>
            ) : (
              ''
            )}
          </Typography>
          {modes.map((m) => (
            <div key={m} className={classes.barRow}>
              <span>{m}</span>
              <div className={classes.track}>
                <div
                  className={classes.fill}
                  style={{ width: `${top ? (byMode[m] / top) * 100 : 0}%` }}
                />
              </div>
              <span className={classes.right}>{inr(byMode[m])}</span>
            </div>
          ))}
          {rows && !today.length && (
            <Typography variant="body2" className={classes.muted}>
              Nothing collected yet today.
            </Typography>
          )}
        </div>
        <div className={classes.overviewBox}>
          <div className={classes.figureKey}>
            {monthName} so far · {picked ? picked.name : 'All teachers'}
          </div>
          <div className={classes.overviewBig}>
            {inr(collected)}{' '}
            <span className={classes.overviewOf}>of {inr(expected)}</span>
          </div>
          <div
            className={classes.track}
            style={{ height: 16, margin: '10px 0 6px' }}
          >
            <div className={classes.fill} style={{ width: `${share}%` }} />
          </div>
          <Typography variant="body2" className={classes.muted}>
            <b>{paid}</b> students have paid {monthName} · <b>{toPay}</b> still
            to pay
          </Typography>
        </div>
      </div>
      <div className={classes.overviewBox} style={{ marginTop: 12 }}>
        <div className={classes.figureKey}>Last payments saved</div>
        {last.map((l) => (
          <div key={l.key} className={classes.lastRow}>
            <span>
              {l.name}
              <span className={classes.cellSub}>
                {[
                  l.no,
                  l.months.join(', '),
                  dayjs(l.at).format('D MMM, h:mm a')
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            <span>{l.mode}</span>
            <b className={classes.right}>{inr(l.amount)}</b>
          </div>
        ))}
        {rows && !last.length && (
          <Typography variant="body2" className={classes.muted}>
            No payment saved in Alfred yet.
          </Typography>
        )}
        {onSeeReceipts && (
          <Button
            size="small"
            color="primary"
            style={{ marginTop: 8 }}
            onClick={onSeeReceipts}
          >
            See all in Daily receipts ›
          </Button>
        )}
      </div>
    </div>
  )
}

// `teacher` is the Teacher filter on top of the page, shared with Collection
// by teacher.
const CollectTab = ({
  teacher: byTeacher,
  teachers,
  onTeachersChanged,
  open,
  onOpened,
  onSeeReceipts
}) => {
  const classes = useStyles()
  const { ledger, failed, load } = useLedger()
  const [q, setQ] = useState('')
  const [student, setStudent] = useState('')
  const [picked, setPicked] = useState('')
  const [adding, setAdding] = useState(false)

  const groups = useMemo(() => {
    const byKey = new Map()
    ;((ledger && ledger.students) || []).forEach((s) => {
      const key = studentKey(s)
      if (!byKey.has(key)) {
        byKey.set(key, { key, name: s.student_name, due: 0, lines: [] })
      }
      const group = byKey.get(key)
      group.due += s.due
      group.lines.push(s)
    })
    return [...byKey.values()]
  }, [ledger])

  const select = useCallback(
    (enrolment, data) => {
      const found = ((data || ledger || {}).students || []).find(
        (s) => s.enrolment === enrolment
      )
      if (!found) return
      setStudent(studentKey(found))
      setPicked(enrolment)
      setAdding(false)
    },
    [ledger]
  )

  // Arriving from a due cell on the teacher sheet.
  useEffect(() => {
    if (open && ledger) {
      select(open)
      onOpened()
    }
  }, [open, ledger, select, onOpened])

  // The student list runs down to the bottom of the window: measure where it
  // starts and leave room for the "Showing 60 of …" line and the Add button.
  const listRef = useRef(null)
  const listFootRef = useRef(null)
  const fitList = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY
    const foot = listFootRef.current ? listFootRef.current.offsetHeight : 0
    el.style.maxHeight = `${Math.max(
      160,
      window.innerHeight - top - foot - 40
    )}px`
  }, [])
  React.useLayoutEffect(fitList)
  useEffect(() => {
    window.addEventListener('resize', fitList)
    return () => window.removeEventListener('resize', fitList)
  }, [fitList])

  if (!ledger) {
    return <Loading failed={failed} classes={classes} what="the students" />
  }

  const term = q.trim().toLowerCase()
  const matches = groups.filter(
    (g) =>
      (g.key.includes(term) ||
        (phoneDigits(term).length >= 3 &&
          g.lines.some((l) =>
            phoneDigits(l.parent_contact).includes(phoneDigits(term))
          ))) &&
      (!byTeacher || g.lines.some((l) => l.teacher_uuid === byTeacher))
  )
  const group = groups.find((g) => g.key === student)
  const joiningLabel = dayjs(`${ledger.current_month}-01`).format("MMM'YY")

  const openGroup = (g) => {
    // With a teacher chosen, open that teacher's line of the student.
    const lines = g.lines.filter(
      (l) => !byTeacher || l.teacher_uuid === byTeacher
    )
    const first = lines.find((l) => l.due > 0) || lines[0] || g.lines[0]
    setStudent(g.key)
    setPicked(first.enrolment)
  }

  const onAdded = async (enrolment, teacherAdded) => {
    if (teacherAdded) onTeachersChanged()
    const data = await load()
    setQ('')
    if (data) select(enrolment, data)
    else setAdding(false)
  }

  return (
    <div className={classes.split}>
      <div className={classes.panel}>
        {adding ? (
          <AddStudent
            startName={matches.length ? '' : q.trim()}
            teachers={teachers}
            joiningLabel={joiningLabel}
            onCancel={() => setAdding(false)}
            onAdded={onAdded}
            classes={classes}
          />
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Find student"
              placeholder="Type a name or phone number"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {matches.length > 0 ? (
              <div className={classes.list} ref={listRef}>
                {matches.slice(0, LIST_LIMIT).map((g) => (
                  <div
                    key={g.key}
                    role="button"
                    tabIndex={0}
                    className={`${classes.listRow} ${
                      g.key === student ? classes.listRowOn : ''
                    }`}
                    onClick={() => openGroup(g)}
                    onKeyDown={(e) => e.key === 'Enter' && openGroup(g)}
                  >
                    <span>
                      {g.name}
                      <span className={classes.cellSub}>
                        {[
                          ...new Set(g.lines.map((l) => l.teacher)),
                          ...new Set(
                            g.lines.map((l) => l.parent_contact).filter(Boolean)
                          )
                        ].join(' · ')}
                      </span>
                    </span>
                    <Chip
                      size="small"
                      className={g.due ? classes.pillDue : classes.pillPaid}
                      label={g.due ? `${inr(g.due)} due` : 'Paid'}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Typography
                variant="body2"
                className={classes.muted}
                style={{ marginTop: 12 }}
              >
                No student called &quot;{q.trim()}&quot;
                {byTeacher ? ' under this teacher' : ''}.
              </Typography>
            )}
            <div ref={listFootRef}>
              {matches.length > LIST_LIMIT && (
                <Typography variant="caption" className={classes.muted}>
                  Showing {LIST_LIMIT} of {matches.length}. Type more of the
                  name to narrow it down.
                </Typography>
              )}
              <Button
                fullWidth
                variant={matches.length ? 'outlined' : 'contained'}
                color="primary"
                size="small"
                style={{ marginTop: 12 }}
                onClick={() => setAdding(true)}
              >
                + Add new student
              </Button>
            </div>
          </>
        )}
      </div>

      <div className={classes.panel}>
        {group ? (
          <>
            <div className={classes.closeBar}>
              <Button
                size="small"
                variant="outlined"
                title="Close this student and go back to today's summary"
                onClick={() => {
                  setStudent('')
                  setPicked('')
                }}
              >
                ✕ Close
              </Button>
            </div>
            <CollectCard
              lines={group.lines}
              picked={picked}
              onPick={setPicked}
              ledger={ledger}
              onSaved={load}
              classes={classes}
            />
          </>
        ) : (
          <CollectOverview
            ledger={ledger}
            teacher={byTeacher}
            teachers={teachers}
            onSeeReceipts={onSeeReceipts}
            classes={classes}
          />
        )}
      </div>
    </div>
  )
}

// ------------------------------------------------------ collection by teacher

// A cell carries only the amount, so the opening and twelve months fit the
// page. How and when it was paid shows on hover, and a click on a paid cell
// opens the detail (with its receipt) inside the cell.
const SheetCell = ({ cell, onCollect, classes }) => {
  const [open, setOpen] = useState(false)
  if (cell.state === 'sheet') {
    return (
      <td
        className={classes.cellSheet}
        title="Kept in the finance sheet. Anything unpaid is in Opening."
      >
        in sheet
      </td>
    )
  }
  if (cell.state === 'na') {
    return <td className={`${classes.cell} ${classes.cellOff}`}>–</td>
  }
  const isOpening = cell.month === OPENING
  const detail = [cell.mode, cell.date_paid ? shortDate(cell.date_paid) : '']
    .filter(Boolean)
    .join(' · ')

  if (cell.state === 'paid' || cell.state === 'advance') {
    return (
      <td
        className={`${classes.cell} ${classes.cellClick} ${
          cell.state === 'advance' ? classes.cellAdvance : classes.cellPaid
        }`}
        title={`${
          cell.state === 'advance' ? 'Paid in advance' : 'Paid'
        } · ${detail}. Click for the detail.`}
        onClick={() => setOpen(!open)}
      >
        <b>{inr(cell.paid)}</b>
        {open && (
          <span className={classes.cellSub}>
            {detail}
            {cell.receipt && (
              <>
                {' · '}
                <a
                  href={cell.receipt}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  receipt
                </a>
              </>
            )}
          </span>
        )}
      </td>
    )
  }
  if (cell.state === 'future') {
    return (
      <td className={`${classes.cell} ${classes.cellOff}`}>
        {cell.paid ? `part ${inr(cell.paid)}` : '–'}
      </td>
    )
  }
  const got =
    cell.state === 'part'
      ? `got ${inr(cell.paid)} · ${detail}${
          cell.reason ? ` · reason: ${cell.reason}` : ''
        }`
      : ''
  const colour = isOpening ? classes.cellOpening : classes.cellClosed
  const body = (
    <>
      <b>{inr(cell.short)}</b>
      {cell.state === 'part' && (
        <span className={classes.cellSub}>got {inr(cell.paid)}</span>
      )}
    </>
  )
  const what = isOpening ? 'Opening due' : 'Due, not paid'
  if (cell.closed) {
    return (
      <td
        className={`${classes.cell} ${colour}`}
        title={`${what}. Month signed off by HQ: ask HQ to reopen it to collect this.`}
      >
        {body}
        <span className={classes.cellSub}>closed</span>
      </td>
    )
  }
  if (!onCollect) {
    return (
      <td className={`${classes.cell} ${colour}`} title={got || what}>
        {body}
      </td>
    )
  }
  return (
    <td
      className={`${classes.cell} ${colour} ${classes.cellClick}`}
      title={`${got || what}. Click to collect.`}
      onClick={onCollect}
    >
      {body}
    </td>
  )
}

// Everything about a student that used to need the Students tab: their
// details, their fee, their teacher, and whether they have left.
const StudentDialog = ({
  student,
  teachers,
  today,
  onClose,
  onSaved,
  classes
}) => {
  const [form, setForm] = useState({
    student_name: student.student_name,
    class_label: student.class_label || '',
    centre: student.centre || '',
    monthly_fee: student.monthly_fee || '',
    parent_contact: student.parent_contact || '',
    teacher: student.teacher_uuid
  })
  const thisMonth = dayjs(today).format('YYYY-MM')
  const [status, setStatus] = useState({
    status: student.status || (student.dropped ? 'discontinued' : 'studying'),
    status_month: student.status_month || thisMonth,
    back_month: student.back_month || '',
    status_reason: student.status_reason || ''
  })
  const [mock, setMock] = useState({
    is_mock: Boolean(student.is_mock),
    mock_from: student.mock_from || thisMonth,
    mock_until: student.mock_until || thisMonth,
    once: Boolean(student.mock_total),
    mock_total: student.mock_total ? String(student.mock_total) : ''
  })
  const [busy, setBusy] = useState(false)
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })
  const setS = (key) => (e) => setStatus({ ...status, [key]: e.target.value })
  const setM = (key) => (e) => setMock({ ...mock, [key]: e.target.value })

  const phoneOk = phoneDigits(form.parent_contact).length >= 10
  const leaving = status.status !== 'studying'
  const reasonOk = !leaving || status.status_reason.trim()
  const backOk =
    status.status !== 'drop' ||
    !status.back_month ||
    status.back_month > status.status_month
  const mockOk =
    !mock.is_mock ||
    (mock.mock_until >= mock.mock_from &&
      (!mock.once || Number(mock.mock_total) > 0))
  let saveLabel = 'Save'
  if (!form.student_name.trim()) saveLabel = 'Type the name'
  else if (!phoneOk) saveLabel = 'Add the parent phone'
  else if (!reasonOk) saveLabel = 'Write the reason'
  else if (!backOk) saveLabel = 'Check "Back in"'
  else if (!mockOk) saveLabel = 'Check the mock months'
  const ready =
    !busy &&
    form.student_name.trim() &&
    Number(form.monthly_fee) > 0 &&
    phoneOk &&
    reasonOk &&
    backOk &&
    mockOk

  const save = async () => {
    setBusy(true)
    try {
      await updateAcadsStudent({
        uuid: student.enrolment,
        ...form,
        status: status.status,
        status_month: leaving ? status.status_month : '',
        back_month: status.status === 'drop' ? status.back_month : '',
        status_reason: leaving ? status.status_reason.trim() : '',
        is_mock: mock.is_mock,
        mock_from: mock.is_mock ? mock.mock_from : '',
        mock_until: mock.is_mock ? mock.mock_until : '',
        mock_total: mock.is_mock && mock.once ? mock.mock_total : ''
      })
      showToast(`Saved ${form.student_name}.`, constants.SUCCESS)
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not save the student'), constants.ERROR)
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>{student.student_name}</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <TextField
            label="Student name"
            size="small"
            variant="outlined"
            value={form.student_name}
            onChange={set('student_name')}
          />
          <TextField
            select
            label="Teacher"
            size="small"
            variant="outlined"
            value={form.teacher}
            onChange={set('teacher')}
            helperText="Months already collected stay with the earlier teacher."
          >
            {teachers.map((t) => (
              <MenuItem key={t.uuid} value={t.uuid}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Monthly fee"
            type="number"
            size="small"
            variant="outlined"
            value={form.monthly_fee}
            onChange={set('monthly_fee')}
            helperText="A new fee applies to months with no payment yet."
          />
          <TextField
            label="Class"
            size="small"
            variant="outlined"
            value={form.class_label}
            onChange={set('class_label')}
          />
          <TextField
            select
            label="Centre"
            size="small"
            variant="outlined"
            value={form.centre}
            onChange={set('centre')}
          >
            <MenuItem value="TR">Theatre Road</MenuItem>
            <MenuItem value="SL">Salt Lake</MenuItem>
          </TextField>
          <TextField
            label="Parent phone"
            size="small"
            variant="outlined"
            required
            value={form.parent_contact}
            onChange={set('parent_contact')}
            error={!phoneOk}
            helperText={
              phoneOk
                ? ' '
                : 'Needed to save: it tells apart two children with the same name.'
            }
            inputProps={{ inputMode: 'tel' }}
          />
          <div style={{ gridColumn: '1 / -1' }}>
            <div className={classes.label} style={{ marginTop: 0 }}>
              Status
            </div>
            <div className={classes.chips}>
              {[
                ['studying', 'Studying'],
                ['drop', 'Drop (break)'],
                ['discontinued', 'Discontinued']
              ].map(([value, label]) => (
                <Chip
                  key={value}
                  label={label}
                  clickable
                  color={status.status === value ? 'primary' : 'default'}
                  onClick={() => setStatus({ ...status, status: value })}
                />
              ))}
            </div>
          </div>
          {leaving && (
            <>
              <TextField
                type="month"
                label={
                  status.status === 'drop' ? 'Drop from' : 'Discontinued from'
                }
                size="small"
                variant="outlined"
                required
                value={status.status_month}
                onChange={setS('status_month')}
                InputLabelProps={{ shrink: true }}
                helperText="No fee from this month."
              />
              {status.status === 'drop' ? (
                <TextField
                  type="month"
                  label="Back in (if known)"
                  size="small"
                  variant="outlined"
                  value={status.back_month}
                  onChange={setS('back_month')}
                  InputLabelProps={{ shrink: true }}
                  error={!backOk}
                  helperText={
                    backOk
                      ? 'Fees start again from this month.'
                      : 'Has to be after the drop month.'
                  }
                />
              ) : (
                <div />
              )}
              <TextField
                label="Reason"
                size="small"
                variant="outlined"
                required
                style={{ gridColumn: '1 / -1' }}
                value={status.status_reason}
                onChange={setS('status_reason')}
                error={!reasonOk}
                helperText={
                  status.status === 'drop'
                    ? 'Why the student is taking a break.'
                    : 'Why the student left.'
                }
              />
            </>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <Chip
              label="Mock / short-term student"
              clickable
              color={mock.is_mock ? 'primary' : 'default'}
              onClick={() => setMock({ ...mock, is_mock: !mock.is_mock })}
              title="Comes only for some months, for example for mocks"
            />
          </div>
          {mock.is_mock && (
            <>
              <TextField
                type="month"
                label="From"
                size="small"
                variant="outlined"
                value={mock.mock_from}
                onChange={setM('mock_from')}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="month"
                label="Until"
                size="small"
                variant="outlined"
                value={mock.mock_until}
                onChange={setM('mock_until')}
                InputLabelProps={{ shrink: true }}
                error={mock.mock_until < mock.mock_from}
                helperText="Nothing is expected after this month."
              />
              <div style={{ gridColumn: '1 / -1' }}>
                <div className={classes.chips}>
                  <Chip
                    label="Monthly fee each month"
                    clickable
                    color={mock.once ? 'default' : 'primary'}
                    onClick={() => setMock({ ...mock, once: false })}
                  />
                  <Chip
                    label="One amount for the whole mock"
                    clickable
                    color={mock.once ? 'primary' : 'default'}
                    onClick={() => setMock({ ...mock, once: true })}
                  />
                </div>
              </div>
              {mock.once && (
                <TextField
                  label="Amount for the whole mock (₹)"
                  size="small"
                  variant="outlined"
                  value={mock.mock_total}
                  onChange={(e) =>
                    setMock({
                      ...mock,
                      mock_total: e.target.value.replace(/[^0-9]/g, '')
                    })
                  }
                  error={!(Number(mock.mock_total) > 0)}
                  helperText="Due in the From month."
                  inputProps={{ inputMode: 'numeric' }}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!ready}
          onClick={save}
        >
          {busy ? 'Saving…' : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const TeacherSheetTab = ({
  teacher,
  teachers,
  onCollect,
  onStudentsChanged,
  readOnly
}) => {
  const classes = useStyles()
  const [year, setYear] = useState('')
  const [withLeft, setWithLeft] = useState(false)
  const [dueOnly, setDueOnly] = useState(false)
  const { ledger, failed, load } = useLedger(year, withLeft)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState(null)

  if (!ledger) {
    return <Loading failed={failed} classes={classes} what="the sheet" />
  }

  const all = ledger.students.filter(
    (s) => !teacher || s.teacher_uuid === teacher
  )
  const term = q.trim().toLowerCase()
  const rows = all.filter((s) => s.student_name.toLowerCase().includes(term))
  const collected = (month) =>
    rows.reduce((sum, s) => {
      const cell = s.cells.find((c) => c.month === month)
      return sum + ((cell && cell.paid) || 0)
    }, 0)
  const totalDue = rows.reduce((sum, s) => sum + s.due, 0)
  const openingDue = rows.reduce((sum, s) => sum + (s.cells[0].short || 0), 0)
  const thisMonth = ledger.months.find((m) => m.month === ledger.current_month)

  // Due list: only students who owe, only the months with something due.
  const dueRows = rows.filter((s) => s.due > 0)
  const dueCols = [{ month: OPENING, label: 'Opening' }, ...ledger.months]
    .map((m) => ({
      month: m.month,
      label: m.label,
      total: dueRows.reduce((sum, s) => {
        const cell = s.cells.find((c) => c.month === m.month)
        return sum + (cell ? dueOf(cell) : 0)
      }, 0)
    }))
    .filter((m) => m.total > 0)
  const teacherName = teacher
    ? ((teachers || []).find((t) => t.uuid === teacher) || {}).name ||
      'One teacher'
    : 'All teachers'
  const downloadDueList = () => {
    const asOn = dayjs(ledger.today).format('D MMM YYYY')
    dueListPdf({
      title: `ACADS due list · ${teacherName}`,
      subtitle: `As on ${asOn} · ${dueRows.length} students · total due ${rs(
        dueRows.reduce((sum, s) => sum + s.due, 0)
      )}${q.trim() ? ` · search "${q.trim()}"` : ''}`,
      cols: dueCols,
      rows: dueRows,
      showTeacher: !teacher
    }).save(
      `ACADS due list ${teacherName} ${dayjs(ledger.today).format(
        'D MMM YYYY'
      )}.pdf`
    )
  }

  return (
    <div>
      <div className={classes.figures}>
        {(ledger.years || []).length > 1 && (
          <TextField
            select
            size="small"
            variant="outlined"
            label="Year"
            value={ledger.year}
            onChange={(e) => setYear(e.target.value)}
          >
            {ledger.years.map((y) => (
              <MenuItem key={y.year} value={y.year}>
                {y.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <div>
          <div className={classes.figureKey}>Students</div>
          <div className={classes.figureVal}>
            {rows.length}
            {rows.length !== all.length ? ` of ${all.length}` : ''}
          </div>
        </div>
        {thisMonth && (
          <div>
            <div className={classes.figureKey}>
              Collected for {thisMonth.label}
            </div>
            <div className={classes.figureVal}>
              {inr(collected(thisMonth.month))}
            </div>
          </div>
        )}
        <div>
          <div className={classes.figureKey}>Opening still due</div>
          <div className={classes.figureVal}>{inr(openingDue)}</div>
        </div>
        <div>
          <div className={classes.figureKey}>Total still due</div>
          <div className={classes.figureVal}>{inr(totalDue)}</div>
        </div>
        <div>
          <div className={classes.figureKey}>Students with dues</div>
          <div className={classes.figureVal}>
            {rows.filter((s) => s.due > 0).length}
          </div>
        </div>
        <Chip
          label="Show dropped and discontinued"
          clickable
          color={withLeft ? 'primary' : 'default'}
          onClick={() => setWithLeft(!withLeft)}
          title="Students who left and owe nothing are hidden until this is on"
        />
        <Chip
          label="Due list"
          clickable
          color={dueOnly ? 'primary' : 'default'}
          onClick={() => setDueOnly(!dueOnly)}
          title="Show only students who owe, and only the amounts due"
        />
        {dueOnly && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={!dueRows.length}
            onClick={downloadDueList}
          >
            Download PDF
          </Button>
        )}
      </div>
      {editing && (
        <StudentDialog
          student={editing}
          teachers={teachers || []}
          today={ledger.today}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
            if (onStudentsChanged) onStudentsChanged()
          }}
          classes={classes}
        />
      )}

      {dueOnly ? (
        <div className={classes.scroll}>
          <table className={`${classes.table} ${classes.sheetTable}`}>
            <thead>
              <tr>
                <th
                  className={`${classes.frozen} ${classes.corner} ${classes.edge}`}
                  style={{ left: 0, minWidth: SHEET_NAME_W }}
                >
                  <TextField
                    className={classes.find}
                    variant="outlined"
                    size="small"
                    placeholder="Student"
                    title="Type to search students"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </th>
                {dueCols.map((m) => (
                  <th key={m.month} className={classes.right}>
                    {m.label}
                  </th>
                ))}
                <th className={classes.right}>Due</th>
              </tr>
            </thead>
            <tbody>
              {dueRows.map((s) => (
                <tr key={s.enrolment}>
                  <td
                    className={`${classes.frozen} ${classes.edge}`}
                    style={{ left: 0, minWidth: SHEET_NAME_W }}
                  >
                    <b>{s.student_name}</b>
                    {s.dropped && (
                      <span
                        className={classes.dropped}
                        title={s.status_reason || undefined}
                      >
                        {s.status === 'drop' ? 'on break' : 'discontinued'}
                      </span>
                    )}
                    {s.is_mock && <span className={classes.dropped}>mock</span>}
                    <span className={classes.cellSub}>
                      {teacher ? '' : `${s.teacher} · `}Class{' '}
                      {s.class_label || '–'} · {s.centre || '–'}
                      {s.parent_contact ? ` · ${s.parent_contact}` : ''}
                    </span>
                  </td>
                  {dueCols.map((m) => {
                    const cell = s.cells.find((c) => c.month === m.month)
                    const v = cell ? dueOf(cell) : 0
                    return (
                      <td
                        key={m.month}
                        className={`${classes.cell} ${
                          v
                            ? m.month === OPENING
                              ? classes.cellOpening
                              : classes.cellClosed
                            : ''
                        }`}
                      >
                        {v ? inr(v) : ''}
                      </td>
                    )
                  })}
                  <td className={classes.cell}>
                    <b>{inr(s.due)}</b>
                  </td>
                </tr>
              ))}
              {dueRows.length === 0 && (
                <tr>
                  <td colSpan={dueCols.length + 2}>
                    Nobody owes anything
                    {q.trim() ? ' in this search' : ' under this teacher'}.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  className={`${classes.frozen} ${classes.corner} ${classes.edge}`}
                  style={{ left: 0 }}
                >
                  Total due
                </td>
                {dueCols.map((m) => (
                  <td key={m.month} className={classes.cell}>
                    {inr(m.total)}
                  </td>
                ))}
                <td className={classes.cell}>
                  {totalDue ? inr(totalDue) : '–'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className={classes.scroll}>
          <table className={`${classes.table} ${classes.sheetTable}`}>
            <thead>
              <tr>
                <th
                  className={`${classes.frozen} ${classes.corner} ${classes.edge}`}
                  style={{ left: 0, minWidth: SHEET_NAME_W }}
                >
                  <TextField
                    className={classes.find}
                    variant="outlined"
                    size="small"
                    placeholder="Student"
                    title="Type to search students"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </th>
                <th
                  className={`${classes.right} ${classes.opening}`}
                  title="Unpaid from before this year"
                >
                  Opening
                </th>
                {ledger.months.map((m) => (
                  <th
                    key={m.month}
                    className={`${classes.right} ${
                      m.month === ledger.current_month ? classes.thisMonth : ''
                    }`}
                  >
                    {m.label}
                    {m.closed ? ' (closed)' : ''}
                  </th>
                ))}
                <th className={classes.right}>Due</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.enrolment}>
                  <td
                    className={`${classes.frozen} ${classes.edge}`}
                    style={{ left: 0, minWidth: SHEET_NAME_W }}
                  >
                    {readOnly ? (
                      <b>{s.student_name}</b>
                    ) : (
                      <b
                        className={classes.nameLink}
                        title="Click to edit this student, or mark a drop or discontinue"
                        onClick={() => setEditing(s)}
                      >
                        {s.student_name}
                      </b>
                    )}
                    {s.dropped && (
                      <span
                        className={classes.dropped}
                        title={s.status_reason || undefined}
                      >
                        {s.status === 'drop' ? 'on break' : 'discontinued'}
                      </span>
                    )}
                    {s.is_mock && <span className={classes.dropped}>mock</span>}
                    <span className={classes.cellSub}>
                      {teacher ? '' : `${s.teacher} · `}Class{' '}
                      {s.class_label || '–'} · {s.centre || '–'} ·{' '}
                      {inr(s.monthly_fee)}
                      {s.parent_contact ? ` · ${s.parent_contact}` : ''}
                    </span>
                  </td>
                  {s.cells.map((c) => (
                    <SheetCell
                      key={c.month}
                      cell={c}
                      onCollect={
                        onCollect ? () => onCollect(s.enrolment) : undefined
                      }
                      classes={classes}
                    />
                  ))}
                  <td className={classes.cell}>
                    <b>{s.due ? inr(s.due) : '–'}</b>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={ledger.months.length + 3}>
                    {all.length
                      ? 'No student matches the search.'
                      : 'No students under this teacher yet.'}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  className={`${classes.frozen} ${classes.corner} ${classes.edge}`}
                  style={{ left: 0 }}
                >
                  Collected
                </td>
                <td className={classes.cell}>
                  {collected(OPENING) ? inr(collected(OPENING)) : '–'}
                </td>
                {ledger.months.map((m) => (
                  <td key={m.month} className={classes.cell}>
                    {collected(m.month) ? inr(collected(m.month)) : '–'}
                  </td>
                ))}
                <td className={classes.cell}>
                  {totalDue ? inr(totalDue) : '–'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <Typography
        variant="caption"
        className={classes.muted}
        style={{ display: 'block', marginTop: 8 }}
      >
        {onCollect ? 'Click a due amount to collect it. ' : ''}Click a paid
        amount to see how it was paid
        {readOnly ? '' : ', and a name to edit the student'}. Opening is what
        was unpaid before this year; money collected goes to Opening first, then
        the oldest month.
        {ledger.months.some((m) => m.in_sheet)
          ? ` Months before ${ledger.entry_start_label} are in the finance sheet.`
          : ''}
      </Typography>
    </div>
  )
}

// ------------------------------------------------------------- daily receipts

const REGISTER_COLS = [
  { key: 'date', label: 'Date of receipt' },
  { key: 'student_name', label: 'Student' },
  { key: 'receipt_no', label: 'Receipt no.' },
  { key: 'centre', label: 'Centre' },
  { key: 'teacher', label: 'Teacher' },
  { key: 'month_label', label: 'Fee month' },
  { key: 'amount', label: 'Amount', numeric: true },
  { key: 'mode', label: 'Mode' },
  { key: 'entered_by', label: 'Entered by' },
  { key: 'receipt', label: 'Receipt', link: true }
]

const FIRST_MONTH = '2026-09' // Alfred's first fee month
const CENTRES = [
  ['', 'Both centres'],
  ['TR', 'Theatre Road'],
  ['SL', 'Salt Lake']
]

const frozenAt = (index) => {
  if (index === 0) return { left: 0, minWidth: DATE_W, maxWidth: DATE_W }
  if (index === 1) return { left: DATE_W, minWidth: 190 }
  return null
}

// Correct a payment already saved. The same fields and the same proof rule
// as Collect a fee: anything that is not cash needs its receipt photo.
const PaymentDialog = ({ row, modes, today, onClose, onSaved, classes }) => {
  const [amount, setAmount] = useState(String(Math.round(row.amount)))
  const [reason, setReason] = useState(row.amount_reason || '')
  const [mode, setMode] = useState(row.mode)
  const [datePaid, setDatePaid] = useState(row.date_paid || today)
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const fileInput = useRef(null)

  const hasProof = row.receipts.length > 0
  const needsFile = Boolean(mode) && mode !== CASH && !hasProof
  const typed = Number(amount) || 0
  let saveLabel = 'Save change'
  if (!typed) saveLabel = 'Type the amount'
  else if (typed > row.fee) saveLabel = 'Check the amount'
  else if (typed < row.fee && !reason.trim()) saveLabel = 'Give the reason'
  else if (!mode) saveLabel = 'Pick how they paid'
  else if (needsFile && !file) saveLabel = 'Attach the receipt'
  const needsReason = typed > 0 && typed < row.fee
  const ready =
    typed > 0 &&
    typed <= row.fee &&
    (!needsReason || reason.trim()) &&
    mode &&
    (file || !needsFile) &&
    !busy

  const save = async () => {
    setBusy(true)
    try {
      await amendAcadsPayment(
        {
          uuid: row.uuid,
          amount: typed,
          mode,
          date_paid: datePaid,
          reason: needsReason ? reason.trim() : ''
        },
        file
      )
      showToast('Payment changed.', constants.SUCCESS)
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not change the payment'), constants.ERROR)
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>
        {row.student_name} · {row.month_label}
        {row.receipt_no !== '–' ? ` · ${row.receipt_no}` : ''}
      </DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <TextField
            label="Amount received"
            type="number"
            size="small"
            variant="outlined"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={typed > row.fee}
            helperText={
              typed > row.fee
                ? `More than the ${inr(
                    row.fee
                  )} due on this line. Collect the extra separately.`
                : `Due on this line: ${inr(row.fee)}`
            }
          />
          {needsReason && (
            <TextField
              label="Reason the amount is not the full fee"
              size="small"
              variant="outlined"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!reason.trim()}
              helperText="Needed because this month stays part-paid."
            />
          )}
          <div>
            <div className={classes.label} style={{ marginTop: 0 }}>
              How they paid
            </div>
            <div className={classes.chips}>
              {modes.map((m) => (
                <Chip
                  key={m}
                  label={m}
                  clickable
                  color={mode === m ? 'primary' : 'default'}
                  onClick={() => setMode(m)}
                />
              ))}
            </div>
          </div>
          <TextField
            label="Date of receipt"
            type="date"
            size="small"
            variant="outlined"
            value={datePaid}
            onChange={(e) => setDatePaid(e.target.value)}
            inputProps={{ max: today }}
            InputLabelProps={{ shrink: true }}
          />
          <div>
            <div className={classes.label} style={{ marginTop: 0 }}>
              Receipt photo{' '}
              <span className={classes.must}>
                {needsFile
                  ? `· needed for ${mode}`
                  : '· needed for everything except cash'}
              </span>
            </div>
            <div className={classes.attach}>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  setFile(e.target.files[0] || null)
                  e.target.value = ''
                }}
              />
              <Button
                size="small"
                variant={needsFile && !file ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => fileInput.current.click()}
              >
                {file ? 'Change photo' : 'Attach photo'}
              </Button>
              {file && (
                <Chip label={file.name} onDelete={() => setFile(null)} />
              )}
              {!file && hasProof && (
                <a
                  href={row.receipts[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View the receipt already attached
                </a>
              )}
              {!file && !hasProof && (
                <Typography variant="caption" className={classes.muted}>
                  A photo or screenshot (JPG or PNG).
                </Typography>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!ready}
          onClick={save}
        >
          {saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const CancelDialog = ({ row, onClose, onSaved }) => {
  const [busy, setBusy] = useState(false)
  const cancel = async () => {
    setBusy(true)
    try {
      await cancelAcadsPayment(row.uuid)
      showToast('Payment removed. The line is unpaid again.', constants.SUCCESS)
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not remove the payment'), constants.ERROR)
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>Remove this payment?</DialogTitle>
      <DialogContent>
        <Typography>
          {inr(row.amount)} · {row.mode} · {row.date} for {row.student_name},{' '}
          {row.month_label}.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          The line goes back to unpaid and its receipt is removed. Collect it
          again if the money was received.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Keep it
        </Button>
        <Button color="secondary" disabled={busy} onClick={cancel}>
          Remove payment
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// The centre cannot unfreeze a month. It asks HQ, and has to say why.
const ReopenDialog = ({ row, onClose, onSaved, classes }) => {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const monthLabel = dayjs(row.date_paid).format('MMMM YYYY')
  const send = async () => {
    setBusy(true)
    try {
      await requestAcadsReopen({
        month: dayjs(row.date_paid).format('YYYY-MM'),
        reason: `${row.student_name}, ${row.month_label}, ${inr(
          row.amount
        )}: ${reason.trim()}`
      })
      showToast(`Request sent to HQ for ${monthLabel}.`, constants.SUCCESS)
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not send the request'), constants.ERROR)
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>Ask HQ to reopen {monthLabel}</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <Typography variant="body2">
            {monthLabel} is frozen, so this payment cannot be changed. Tell HQ
            what needs to change. HQ will reopen the month or reply.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={3}
            label="What needs to change? (required)"
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={busy || !reason.trim()}
          onClick={send}
        >
          {reason.trim() ? 'Send to HQ' : 'Write the reason'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// `readOnly` is the HQ replica: the same list with nothing to change.
// `filterSlot` is the page's top bar: Centre and Period are drawn there, so
// every tab keeps its filters in the same place. `fixed` ({ month, centre })
// is the HQ view, where the page owns one period and centre for all its tabs.

// ------------------------------------------------------------ dispatch sheet

// Plain "Rs" because the PDF's built-in font has no ₹ sign.
const pdfRs = (v) =>
  `Rs ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

const PAID_TO_TEACHER = 'Paid to teacher'

// What one teacher is owed for the fees received in a month, the way the
// Kolkata dispatch sheet works it out: their share of everything received,
// less what parents already paid the teacher directly.
const dispatchFigures = (rows, share, split) => {
  const total = rows.reduce((sum, r) => sum + r.amount, 0)
  const teacherShare = Math.round(total * share)
  const paidToTeacher = rows
    .filter((r) => r.mode === PAID_TO_TEACHER)
    .reduce((sum, r) => sum + r.amount, 0)
  const payable = teacherShare - paidToTeacher
  // OpenHouse keeps the rest: its share plus 18% GST on that share. GST is
  // worked out as the remainder so the three always add up to total fees.
  const ohShare = Math.round((total - teacherShare) / 1.18)
  const gst = total - teacherShare - ohShare
  return {
    total,
    teacherShare,
    ohShare,
    gst,
    paidToTeacher,
    payable,
    bank: split ? split.bank : null,
    cash: split ? split.cash : null
  }
}

const DISPATCH_COLS = [
  ['S.No.', 30, 'l'],
  ['Centre', 40, 'l'],
  ['Student name', 150, 'l'],
  ['Class', 36, 'l'],
  ['Joining', 52, 'l'],
  ['Fee month', 58, 'l'],
  ['Fees', 56, 'r'],
  ['Parent phone', 80, 'l'],
  ['Status', 52, 'l'],
  ['Date of payment', 76, 'l'],
  ['Amount paid', 64, 'r'],
  ['Mode', 84, 'l']
]

// One section per teacher, each starting on a new page.
const dispatchPdf = ({ monthLabel, groups }) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' })
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 32
  const rowH = 16
  const xs = []
  DISPATCH_COLS.reduce((x, [, w]) => {
    xs.push(x)
    return x + w
  }, margin)
  const tableW = DISPATCH_COLS.reduce((sum, [, w]) => sum + w, 0)
  let y = margin

  const cellText = (i, value) => {
    const [, w, align] = DISPATCH_COLS[i]
    const text = doc.splitTextToSize(String(value || ''), w - 6)[0] || ''
    if (align === 'r') doc.text(text, xs[i] + w - 4, y + 11, { align: 'right' })
    else doc.text(text, xs[i] + 3, y + 11)
  }
  const header = () => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7.5)
    doc.setFillColor(241, 242, 244)
    doc.rect(margin, y, tableW, rowH + 2, 'F')
    DISPATCH_COLS.forEach(([label], i) => cellText(i, label))
    y += rowH + 2
  }

  groups.forEach((g, gi) => {
    if (gi > 0) doc.addPage()
    y = margin
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.text(g.teacher, margin, y + 10)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(102, 112, 133)
    doc.text(
      `Dispatch · fees received in ${monthLabel} · ${g.rows.length} receipt lines`,
      margin,
      y + 26
    )
    doc.setTextColor(0, 0, 0)
    y += 38
    header()

    let last = null
    let n = 0
    g.rows.forEach((r) => {
      if (y + rowH > pageH - margin) {
        doc.addPage()
        y = margin
        header()
      }
      if (r.month !== last) {
        n = 0
        if (last !== null) {
          doc.setDrawColor(160, 166, 176)
          doc.line(margin, y, margin + tableW, y)
        }
        last = r.month
      }
      n += 1
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7.5)
      ;[
        n,
        r.student_centre || r.centre,
        r.student_name,
        r.class_label,
        r.joining,
        r.month_label,
        r.fee ? pdfRs(r.fee) : '',
        r.phone,
        r.status === 'Part paid' ? 'Part paid' : 'Paid',
        r.date_paid ? dayjs(r.date_paid).format('D MMM YYYY') : '',
        pdfRs(r.amount),
        r.mode
      ].forEach((v, i) => cellText(i, v))
      doc.setDrawColor(228, 231, 236)
      doc.line(margin, y + rowH, margin + tableW, y + rowH)
      y += rowH
    })

    const f = g.figures
    const lines = [
      ['Total fees', pdfRs(f.total), true],
      [
        `Teacher's share (${+(g.share * 100).toFixed(3)}%)`,
        pdfRs(f.teacherShare)
      ],
      ['OH share', pdfRs(f.ohShare)],
      ['GST on OH share (18%)', pdfRs(f.gst)],
      ['Paid to teacher', pdfRs(f.paidToTeacher)],
      ['Total payable', pdfRs(f.payable), true],
      ['To be paid through bank', f.bank === null ? '' : pdfRs(f.bank)],
      ['To be paid through cash', f.cash === null ? '' : pdfRs(f.cash)]
    ]
    if (y + 12 + lines.length * 16 > pageH - margin) {
      doc.addPage()
      y = margin
    }
    y += 12
    // Lined up with the table's left edge, label then amount.
    const labelX = margin + 3
    const valueX = margin + 260
    lines.forEach(([label, value, bold]) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal')
      doc.setFontSize(9)
      doc.text(label, labelX, y + 11)
      if (value) {
        doc.text(value, valueX, y + 11, { align: 'right' })
      } else {
        doc.setDrawColor(160, 166, 176)
        doc.line(valueX - 86, y + 12, valueX, y + 12)
      }
      y += 16
    })
  })
  return doc
}

const DispatchDialog = ({ rows, today, startMonth, onClose, classes }) => {
  const teachers = useMemo(() => {
    const seen = new Map()
    rows.forEach((r) => {
      if (!seen.has(r.teacher_uuid)) {
        seen.set(r.teacher_uuid, { uuid: r.teacher_uuid, name: r.teacher })
      }
    })
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])
  const months = []
  for (
    let m = dayjs(today).startOf('month');
    m.format('YYYY-MM') >= FIRST_MONTH;
    m = m.subtract(1, 'month')
  ) {
    months.push([m.format('YYYY-MM'), m.format('MMMM YYYY')])
  }
  const [teacher, setTeacher] = useState('')
  const [month, setMonth] = useState(
    months.some(([m]) => m === startMonth)
      ? startMonth
      : (months[1] || months[0])[0]
  )
  const [bank, setBank] = useState('')
  const [cash, setCash] = useState('')
  const [busy, setBusy] = useState(false)
  // A split saved earlier for this teacher and month comes back filled in.
  useEffect(() => {
    if (!teacher) return undefined
    let live = true
    fetchAcadsDispatch(month)
      .then((res) => {
        const saved =
          live && res.teachers.find((t) => t.teacher_uuid === teacher)
        if (saved && saved.bank !== null) {
          setBank(String(Math.round(saved.bank)))
          setCash(String(Math.round(saved.cash)))
        }
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [teacher, month])

  const picked = rows
    .filter(
      (r) => r.date_paid && dayjs(r.date_paid).format('YYYY-MM') === month
    )
    .filter((r) => r.teacher_uuid)
  const groups = teachers
    .filter((t) => (teacher ? t.uuid === teacher : true))
    .map((t) => {
      const mine = picked
        .filter((r) => r.teacher_uuid === t.uuid)
        .sort(
          (a, b) =>
            (a.month || '').localeCompare(b.month || '') ||
            a.student_name.localeCompare(b.student_name)
        )
      const share = mine.length ? mine[0].teacher_share : 0.66413
      return {
        teacher: t.name,
        share,
        rows: mine,
        figures: dispatchFigures(
          mine,
          share,
          teacher && (bank !== '' || cash !== '')
            ? { bank: Number(bank) || 0, cash: Number(cash) || 0 }
            : null
        )
      }
    })
    .filter((g) => g.rows.length)
  const one = teacher && groups[0]
  // Bank and cash: typing one fills the other with the rest of the payable;
  // either can then be changed, but together they must make the payable.
  const splitTyped = bank !== '' || cash !== ''
  const splitSum = (Number(bank) || 0) + (Number(cash) || 0)
  const splitOk = !one || !splitTyped || splitSum === one.figures.payable
  const typeSplit = (value, setThis, setOther) => {
    const v = value.replace(/[^0-9]/g, '')
    setThis(v)
    if (!one) return
    setOther(
      v === '' ? '' : String(Math.max(one.figures.payable - Number(v), 0))
    )
  }
  const monthLabel = (months.find(([m]) => m === month) || [])[1] || month

  const download = async () => {
    const name = one ? groups[0].teacher : 'All teachers'
    // The bank / cash split is saved too, so HQ's payout table shows it.
    if (one && splitTyped) {
      setBusy(true)
      try {
        await saveAcadsDispatchSplit({
          teacher,
          month,
          bank: Number(bank) || 0,
          cash: Number(cash) || 0
        })
        showToast('Bank and cash split saved.', constants.SUCCESS)
      } catch (err) {
        showToast(errorText(err, 'Could not save the split'), constants.ERROR)
        setBusy(false)
        return
      }
      setBusy(false)
    }
    dispatchPdf({ monthLabel, groups }).save(
      `Dispatch ${name} ${monthLabel}.pdf`
    )
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Dispatch sheet</DialogTitle>
      <DialogContent>
        <div style={{ display: 'grid', gap: 14, paddingTop: 6 }}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Teacher"
            value={teacher}
            onChange={(e) => {
              setTeacher(e.target.value)
              setBank('')
              setCash('')
            }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All teachers, one page each</MenuItem>
            {teachers.map((t) => (
              <MenuItem key={t.uuid} value={t.uuid}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Fees received in"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            helperText="By date of receipt, like the dispatch sheet."
          >
            {months.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {one && (
            <>
              <Typography variant="body2">
                Total fees <b>{inr(one.figures.total)}</b> · teacher&apos;s
                share <b>{inr(one.figures.teacherShare)}</b> · OH share{' '}
                <b>{inr(one.figures.ohShare)}</b> · GST{' '}
                <b>{inr(one.figures.gst)}</b> · paid to teacher{' '}
                <b>{inr(one.figures.paidToTeacher)}</b> · payable{' '}
                <b>{inr(one.figures.payable)}</b>
              </Typography>
              <div style={{ display: 'flex', gap: 12 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  label="Through bank (₹)"
                  value={bank}
                  onChange={(e) => typeSplit(e.target.value, setBank, setCash)}
                  error={!splitOk}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  label="Through cash (₹)"
                  value={cash}
                  onChange={(e) => typeSplit(e.target.value, setCash, setBank)}
                  error={!splitOk}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </div>
              <Typography
                variant="caption"
                className={splitOk ? classes.muted : undefined}
                style={splitOk ? undefined : { color: '#a23c19' }}
              >
                {!splitTyped
                  ? 'Leave both blank to write the bank and cash split by hand.'
                  : splitOk
                  ? `Bank ${inr(Number(bank) || 0)} + cash ${inr(
                      Number(cash) || 0
                    )} = payable ${inr(one.figures.payable)}.`
                  : `Bank + cash is ${inr(splitSum)}, payable is ${inr(
                      one.figures.payable
                    )}. Make them match.`}
              </Typography>
            </>
          )}
          {!groups.length && (
            <Typography variant="body2" className={classes.muted}>
              No fees received in {monthLabel}
              {teacher ? ' for this teacher' : ''}.
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!groups.length || !splitOk || busy}
          onClick={download}
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const RegisterTab = ({
  readOnly,
  filterSlot,
  fixed,
  teachers,
  onStudentsChanged
}) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [editing, setEditing] = useState(null)
  const [cancelling, setCancelling] = useState(null)
  const [asking, setAsking] = useState(null)
  const [failed, setFailed] = useState(false)
  const [centre, setCentre] = useState('')
  // 'today', 'all', 'custom', or a month as '2026-10'; empty = this month
  const [period, setPeriod] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [find, setFind] = useState({})
  const [limit, setLimit] = useState(REGISTER_PAGE)
  const [dispatching, setDispatching] = useState(false)
  // Clicking a student opens the same pop-up as Collection by teacher.
  const [student, setStudent] = useState(null)
  const openStudent = (enrolment) =>
    fetchAcadsLedger(undefined, true)
      .then((ledger) => {
        const found = ledger.students.find((x) => x.enrolment === enrolment)
        if (found) setStudent({ ...found, today: ledger.today })
        else showToast('Could not find this student.', constants.ERROR)
      })
      .catch(() => showToast('Could not open the student.', constants.ERROR))

  const load = useCallback(
    () =>
      fetchAcadsRegister()
        .then(setData)
        .catch(() => setFailed(true)),
    []
  )
  useEffect(() => {
    load()
  }, [load])
  const changed = () => {
    setEditing(null)
    setCancelling(null)
    setAsking(null)
    load()
  }

  if (!data) {
    return <Loading failed={failed} classes={classes} what="the receipts" />
  }

  const today = dayjs(data.today)
  const thisMonth = today.format('YYYY-MM')
  const chosen = fixed ? fixed.month : period || thisMonth
  const where = fixed ? fixed.centre : centre
  const periods = [['today', `Today, ${today.format('D MMM')}`]]
  for (
    let m = today.startOf('month');
    m.format('YYYY-MM') >= FIRST_MONTH;
    m = m.subtract(1, 'month')
  ) {
    periods.push([m.format('YYYY-MM'), m.format('MMMM YYYY')])
  }
  periods.push(['all', 'Since September'], ['custom', 'Custom dates'])
  const text = (row) => ({
    ...row,
    date: row.date_paid ? shortDate(row.date_paid) : 'no date',
    receipt_no: row.receipt_no || '–',
    amount: String(Math.round(row.amount)),
    // searchable: type "no" in the Receipt box to list rows without proof
    receipt: row.receipts.length ? 'yes' : 'no receipt'
  })
  const rows = data.rows
    .filter((r) => !where || r.centre === where)
    .filter((r) => {
      if (chosen === 'all') return true
      if (!r.date_paid) return false
      const paid = dayjs(r.date_paid)
      if (chosen === 'today') return paid.isSame(today, 'day')
      if (chosen === 'custom') {
        const day = paid.format('YYYY-MM-DD')
        return (!from || day >= from) && (!to || day <= to)
      }
      return paid.format('YYYY-MM') === chosen
    })
    .map(text)
    .filter((r) =>
      REGISTER_COLS.every((c) => {
        const term = (find[c.key] || '').trim().toLowerCase()
        return (
          !term ||
          String(
            c.key === 'student_name'
              ? `${r.student_name} ${r.phone || ''}`
              : r[c.key] || ''
          )
            .toLowerCase()
            .includes(term)
        )
      })
    )

  const total = rows.reduce((sum, r) => sum + r.amount * 1, 0)
  const byMode = {}
  rows.forEach((r) => {
    byMode[r.mode || 'No mode'] = (byMode[r.mode || 'No mode'] || 0) + +r.amount
  })
  const searching = Object.values(find).some((v) => v && v.trim())

  const filters = (
    <>
      <TextField
        select
        size="small"
        variant="outlined"
        label="Centre"
        style={{ minWidth: 150 }}
        value={centre}
        onChange={(e) => setCentre(e.target.value)}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
      >
        {CENTRES.map(([value, label]) => (
          <MenuItem key={label} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        variant="outlined"
        label="Period"
        style={{ minWidth: 170 }}
        value={chosen}
        onChange={(e) => setPeriod(e.target.value)}
      >
        {periods.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {chosen === 'custom' && (
        <>
          <TextField
            type="date"
            size="small"
            variant="outlined"
            label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            inputProps={{ max: to || data.today }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            size="small"
            variant="outlined"
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            inputProps={{ min: from, max: data.today }}
            InputLabelProps={{ shrink: true }}
          />
        </>
      )}
    </>
  )

  return (
    <div>
      {!fixed &&
        (filterSlot ? (
          createPortal(filters, filterSlot)
        ) : (
          <div className={classes.filters} style={{ gap: 12 }}>
            {filters}
          </div>
        ))}

      <div className={classes.summary}>
        <span className={classes.summaryMain}>
          {rows.length} receipt{rows.length === 1 ? '' : 's'} · {inr(total)}
        </span>
        {Object.keys(byMode)
          .sort((x, y) => byMode[y] - byMode[x])
          .map((m) => (
            <Chip
              key={m}
              size="small"
              variant="outlined"
              label={`${m} ${inr(byMode[m])}`}
            />
          ))}
        {searching && (
          <Button size="small" variant="outlined" onClick={() => setFind({})}>
            Clear search
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          color="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => setDispatching(true)}
          title="The teacher's dispatch sheet as a PDF: fees received in a month, their share, and what is payable"
        >
          Dispatch sheet
        </Button>
      </div>
      {student && (
        <StudentDialog
          student={student}
          teachers={teachers || []}
          today={student.today}
          onClose={() => setStudent(null)}
          onSaved={() => {
            setStudent(null)
            load()
            if (onStudentsChanged) onStudentsChanged()
          }}
          classes={classes}
        />
      )}
      {dispatching && (
        <DispatchDialog
          rows={data.rows}
          today={data.today}
          startMonth={/^\d{4}-\d{2}$/.test(chosen) ? chosen : ''}
          onClose={() => setDispatching(false)}
          classes={classes}
        />
      )}

      <div className={classes.scroll}>
        <table className={classes.table}>
          <thead>
            <tr>
              {REGISTER_COLS.map((c, i) => {
                const pin = frozenAt(i)
                return (
                  <th
                    key={c.key}
                    className={[
                      pin ? `${classes.frozen} ${classes.corner}` : '',
                      i === 1 ? classes.edge : ''
                    ].join(' ')}
                    style={pin || undefined}
                  >
                    <TextField
                      className={classes.find}
                      variant="outlined"
                      size="small"
                      placeholder={c.label}
                      title={`Type to search ${c.label.toLowerCase()}`}
                      value={find[c.key] || ''}
                      onChange={(e) =>
                        setFind({ ...find, [c.key]: e.target.value })
                      }
                    />
                  </th>
                )
              })}
              {!readOnly && <th>Change</th>}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, limit).map((r) => (
              <tr key={r.uuid}>
                {REGISTER_COLS.map((c, i) => {
                  const pin = frozenAt(i)
                  return (
                    <td
                      key={c.key}
                      className={[
                        pin ? classes.frozen : '',
                        i === 1 ? classes.edge : '',
                        c.numeric ? classes.cell : ''
                      ].join(' ')}
                      style={pin || undefined}
                      title={c.key === 'student_name' ? r.note : undefined}
                    >
                      {c.link && r.receipts.length === 0 && '–'}
                      {c.link &&
                        r.receipts.map((url, n) => (
                          <a
                            key={url}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginRight: 8 }}
                          >
                            View{r.receipts.length > 1 ? ` ${n + 1}` : ''}
                          </a>
                        ))}
                      {!c.link &&
                        c.key === 'student_name' &&
                        !readOnly &&
                        r.enrolment && (
                          <b
                            className={classes.nameLink}
                            title="Click to open this student"
                            onClick={() => openStudent(r.enrolment)}
                          >
                            {r.student_name}
                          </b>
                        )}
                      {!c.link &&
                        !(
                          c.key === 'student_name' &&
                          !readOnly &&
                          r.enrolment
                        ) &&
                        (c.numeric ? inr(r[c.key]) : r[c.key])}
                      {c.key === 'student_name' && r.phone && (
                        <span className={classes.cellSub}>{r.phone}</span>
                      )}
                      {c.key === 'month_label' && r.status === 'Part paid'
                        ? ' (part)'
                        : ''}
                      {c.key === 'amount' && r.amount_reason && (
                        <span
                          className={classes.cellSub}
                          title={r.amount_reason}
                        >
                          {r.amount_reason}
                        </span>
                      )}
                    </td>
                  )
                })}
                {!readOnly && (
                  <td
                    title={
                      r.can_change || r.frozen
                        ? undefined
                        : 'Entered on an earlier day. HQ Finance can change it.'
                    }
                  >
                    {r.can_change && (
                      <>
                        <Button size="small" onClick={() => setEditing(r)}>
                          Edit
                        </Button>
                        <Button size="small" onClick={() => setCancelling(r)}>
                          Cancel
                        </Button>
                      </>
                    )}
                    {r.frozen && (
                      <>
                        <Chip size="small" label="Frozen" />{' '}
                        {r.reopen_asked ? (
                          <span className={classes.cellSub}>
                            HQ has been asked
                          </span>
                        ) : (
                          <Button size="small" onClick={() => setAsking(r)}>
                            Ask HQ to reopen
                          </Button>
                        )}
                      </>
                    )}
                    {!r.can_change && !r.frozen && '–'}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={REGISTER_COLS.length + (readOnly ? 0 : 1)}>
                  {searching
                    ? 'No receipt matches the search.'
                    : 'No receipts for this choice yet. Save a payment in Collect a fee and it appears here.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && (
        <PaymentDialog
          row={editing}
          modes={data.modes || []}
          today={data.today}
          onClose={() => setEditing(null)}
          onSaved={changed}
          classes={classes}
        />
      )}
      {asking && (
        <ReopenDialog
          row={asking}
          onClose={() => setAsking(null)}
          onSaved={changed}
          classes={classes}
        />
      )}
      {cancelling && (
        <CancelDialog
          row={cancelling}
          onClose={() => setCancelling(null)}
          onSaved={changed}
        />
      )}
      {rows.length > limit && (
        <Button
          size="small"
          variant="outlined"
          style={{ marginTop: 8 }}
          onClick={() => setLimit(limit + REGISTER_PAGE)}
        >
          Show {Math.min(REGISTER_PAGE, rows.length - limit)} more
        </Button>
      )}
    </div>
  )
}

// The Teacher filter on top of the page. Its last line adds a teacher, so a
// new one can be put on the list from wherever the list is being read.
const TeacherPicker = ({ teacher, teachers, onPick, onTeachersChanged }) => {
  const classes = useStyles()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const close = () => {
    setAdding(false)
    setName('')
    setBusy(false)
  }
  const add = async () => {
    setBusy(true)
    try {
      const created = await createAcadsTeacher(name.trim())
      showToast(`Added ${created.name}.`, constants.SUCCESS)
      onTeachersChanged()
      onPick(created.uuid)
      close()
    } catch (err) {
      showToast(errorText(err, 'Could not add the teacher'), constants.ERROR)
      setBusy(false)
    }
  }
  return (
    <>
      <TextField
        select
        size="small"
        variant="outlined"
        label="Teacher"
        style={{ minWidth: 200 }}
        value={teacher}
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
        onChange={(e) =>
          e.target.value === NEW_TEACHER
            ? setAdding(true)
            : onPick(e.target.value)
        }
      >
        <MenuItem value="">All teachers</MenuItem>
        {teachers.map((t) => (
          <MenuItem key={t.uuid} value={t.uuid}>
            {`${t.name} (${t.students})`}
          </MenuItem>
        ))}
        <MenuItem value={NEW_TEACHER}>
          <b>+ Add a teacher</b>
        </MenuItem>
      </TextField>
      {adding && (
        <Dialog open onClose={busy ? undefined : close}>
          <DialogTitle>Add a teacher</DialogTitle>
          <DialogContent>
            <div className={classes.form}>
              <TextField
                autoFocus
                label="Teacher name"
                size="small"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                helperText="Students are added to this teacher from Collect a fee."
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={close}>
              Close
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={busy || !name.trim()}
              onClick={add}
            >
              Add teacher
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export { CollectTab, RegisterTab, TeacherPicker, TeacherSheetTab }
